import { useEffect, useRef } from 'react';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { PAGE_SIZE } from '../../../variables/constant';
import {
  OrderBy,
  useAddTypeformUserMappingMutation,
  useAssignmentsQuery,
  useGetTeacherDashboardIndividualStudentTourTypeformQuery,
} from '../../../generated/graphql';
import usePagination from '../../../utils/usePagination';
import { AssignmentSort, AssignmentTabs, NotifierType } from '../../../variables/types';
import { toUTC } from '../../../utils/dateFormat';
import usePrevious from '../../../utils/usePrevious';
import { trackEvent } from '../../../utils/trackevent';
import { openSnackbar } from '../../../components/Notifier';
import getErrorMessage from '../../../utils/getErrorMessage';

interface Params {
  section?: string;
}

// eslint-disable-next-line @typescript-eslint/default-param-last
const getDateFilter = (section: string = AssignmentTabs.Current, dateString: string) => {
  let start = null;
  let close = null;
  if (section === AssignmentTabs.Past) {
    close = {
      lt: dateString,
    };
  } else if (section === AssignmentTabs.Upcoming) {
    start = {
      gt: dateString,
    };
  } else {
    close = {
      gte: dateString,
    };
    start = {
      lte: dateString,
    };
  }
  return {
    closeAt: close,
    startAt: start,
  };
};

const useAssignments = ({ section }: Params) => {
  const pagination = usePagination();
  const now = useRef(toUTC(new Date()));
  const prevSection = usePrevious(section);
  const [sort, setSort] = useQueryParam('sort', withDefault(StringParam, AssignmentSort.CloseAt));
  const [order, setOrder] = useQueryParam('order', withDefault(StringParam, OrderBy.Desc));
  const { startAt, closeAt } = getDateFilter(section, now.current);
  const { data, loading, refetch: assignmentsRefetch } = useAssignmentsQuery({
    fetchPolicy: 'network-only',
    pollInterval: 60000,
    variables: {
      limit: PAGE_SIZE,
      startAt,
      closeAt,
      page: pagination.page,
      sort,
      orderBy: order as OrderBy,
    },
  });
  
  const { data: typeformDetails, loading: typeformDetailsLoading, refetch: refetchTypeform } = useGetTeacherDashboardIndividualStudentTourTypeformQuery({
    fetchPolicy: 'network-only',
    variables: {},
  });

  const [addTypeformUserMapping, { loading: updateUserLoading }]  = useAddTypeformUserMappingMutation();


  const onSortChange = (sortBy: AssignmentSort, orderBy: OrderBy) => {
    pagination.setPage(1);
    setSort(sortBy);
    setOrder(orderBy);
  };

  useEffect(() => {
    if (prevSection && section !== prevSection) {
      pagination.setPage(1);
    }
  }, [pagination, prevSection, section]);

  const assignments = data?.getAllAssignmentsForTeacher.nodes ?? [];
  const totalCount = data?.getAllAssignmentsForTeacher.totalCount ?? 0;

  // call mutation to add typeform user mapping
  const onTypeformReady = async () => {
    try {
      await addTypeformUserMapping({
        variables: {
          typeformId: typeformDetails?.getTeacherDashboardIndividualStudentTourTypeform?.id ?? '',
          submitted: true,
        },
      });
    } catch (err) {
      openSnackbar({ message: getErrorMessage(err) }, NotifierType.Error);
    }
    refetchTypeform();
  };

  const onTypeformSubmit = async () => {
    trackEvent('tbm_typeform', 'teacher_benchmark_dashboard_typeform_submit');
    try {
      await addTypeformUserMapping({
        variables: {
          typeformId: typeformDetails?.getTeacherDashboardIndividualStudentTourTypeform?.id ?? '',
          submitted: true,
        },
      });
    } catch (err) {
      openSnackbar({ message: getErrorMessage(err) }, NotifierType.Error);
    }
    refetchTypeform();
  };

  return {
    loading : loading || typeformDetailsLoading,
    totalCount,
    assignments, assignmentsRefetch,
    pagination,
    onSortChange,
    sort,
    order: order as OrderBy,
    onTypeformReady,
    onTypeformSubmit,
    updateUserLoading,
    typeformData: typeformDetails?.getTeacherDashboardIndividualStudentTourTypeform ?? null,
  };
};

export default useAssignments;
